// Condição de pagamento padrão do fornecedor (aba Pagamento). É ela que gera as
// parcelas do pedido de compra: cada parcela vence X dias após a emissão ou a
// entrega do pedido e vale um % do total.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import { providerService } from '../../services/providerService';
import type {
  DueDateBase,
  PaymentMethodOption,
  ProviderPaymentTerm,
  ProviderPaymentTermInstallment,
} from '../../types/providerPaymentTerm';
import { DUE_DATE_BASE_DELIVERY, DUE_DATE_BASE_ORDER } from '../../types/providerPaymentTerm';
import {
  addDaysISO,
  buildInstallmentsFromTerm,
  describeTerm,
  installmentsFromDays,
  parseDaysList,
  percentTotal,
  splitPercents,
  validateTerm,
} from '../purchaseOrders/paymentTermUtils';

const todayISO = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const inputCls = 'w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

interface EditorProps {
  isOpen: boolean;
  providerId: number;
  initial: ProviderPaymentTerm | null;
  paymentMethods: PaymentMethodOption[];
  onClose: () => void;
  onSaved: (term: ProviderPaymentTerm | null) => void;
}

const ProviderPaymentTermModal: React.FC<EditorProps> = ({ isOpen, providerId, initial, paymentMethods, onClose, onSaved }) => {
  const [methodId, setMethodId] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<ProviderPaymentTermInstallment[]>([]);
  const [daysText, setDaysText] = useState('');
  const [quickBase, setQuickBase] = useState<DueDateBase>(DUE_DATE_BASE_DELIVERY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setMethodId(initial?.payment_method_id ?? 0);
    setNotes(initial?.notes ?? '');
    setRows(initial?.installments?.length ? initial.installments.map((i) => ({ ...i })) : installmentsFromDays([30], DUE_DATE_BASE_DELIVERY));
    setDaysText(initial?.installments?.map((i) => i.days).join('/') ?? '');
    setError(null);
  }, [isOpen, initial]);

  const validation = validateTerm({ payment_method_id: methodId, installments: rows });
  const total = percentTotal(rows);

  // Exemplo: pedido de R$ 1.000,00 emitido hoje e entregue em 15 dias.
  const example = useMemo(() => {
    if (validation) return null;
    const orderDate = todayISO();
    const deliveryDate = addDaysISO(orderDate, 15);
    return {
      orderDate,
      deliveryDate,
      installments: buildInstallmentsFromTerm({ installments: rows, payment_method_id: methodId }, 1000, orderDate, deliveryDate).installments,
    };
  }, [validation, rows, methodId]);

  const fillFromDays = () => {
    const days = parseDaysList(daysText);
    if (!days) {
      setError('Informe os prazos em dias, separados por barra (ex.: 30/60/90).');
      return;
    }
    if (days.length > 24) {
      setError('No máximo 24 parcelas.');
      return;
    }
    setError(null);
    setRows(installmentsFromDays(days, quickBase));
  };

  const update = (idx: number, patch: Partial<ProviderPaymentTermInstallment>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const splitEqually = () => {
    const pcts = splitPercents(rows.length);
    setRows((prev) => prev.map((r, i) => ({ ...r, percent: pcts[i] })));
  };

  const save = async () => {
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await providerService.savePaymentTerm(providerId, {
        payment_method_id: methodId,
        notes: notes.trim() || undefined,
        installments: rows.map((r) => ({ due_date_base: r.due_date_base, days: r.days, percent: r.percent })),
      });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar a condição de pagamento');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError(null);
    try {
      await providerService.deletePaymentTerm(providerId);
      onSaved(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover a condição de pagamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Condição de pagamento do fornecedor"
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <div>
            {initial && (
              <Button variant="danger" size="sm" onClick={remove} disabled={saving}>
                Remover condição
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={save} loading={saving} disabled={!!validation}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-label block mb-1">Forma de pagamento *</span>
            <select className={inputCls} value={methodId || ''} onChange={(e) => setMethodId(Number(e.target.value) || 0)}>
              <option value="">Selecione…</option>
              {paymentMethods.map((m) => (
                <option key={m.payment_method_id} value={m.payment_method_id}>
                  {m.payment_method_name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-label block mb-1">Observações</span>
            <input className={inputCls} value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: negociado em set/26" />
          </label>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs text-slate-600 mb-2">Preenchimento rápido — prazos iguais, mesma base:</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="text-[11px] text-slate-500 block mb-0.5">Prazos (dias)</span>
              <input className={`${inputCls} w-40`} value={daysText} onChange={(e) => setDaysText(e.target.value)} placeholder="30/60/90" />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-500 block mb-0.5">Contados a partir da</span>
              <select className={inputCls} value={quickBase} onChange={(e) => setQuickBase(Number(e.target.value) as DueDateBase)}>
                <option value={DUE_DATE_BASE_ORDER}>Emissão do pedido</option>
                <option value={DUE_DATE_BASE_DELIVERY}>Entrega do pedido</option>
              </select>
            </label>
            <Button variant="outline" size="sm" onClick={fillFromDays}>
              Preencher
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-label">Parcelas</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={splitEqually} disabled={rows.length === 0}>
                Dividir igualmente
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Plus size={14} />}
                disabled={rows.length >= 24}
                onClick={() => setRows((prev) => [...prev, { due_date_base: DUE_DATE_BASE_DELIVERY, days: 0, percent: 0 }])}
              >
                Parcela
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-1 pr-2">#</th>
                  <th className="py-1 pr-2">Vence após a</th>
                  <th className="py-1 pr-2 w-24">Dias</th>
                  <th className="py-1 pr-2 w-28">% do total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-500">{idx + 1}ª</td>
                    <td className="py-1.5 pr-2">
                      <select className={inputCls} value={r.due_date_base} onChange={(e) => update(idx, { due_date_base: Number(e.target.value) as DueDateBase })}>
                        <option value={DUE_DATE_BASE_ORDER}>Emissão do pedido</option>
                        <option value={DUE_DATE_BASE_DELIVERY}>Entrega do pedido</option>
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        className={inputCls}
                        inputMode="numeric"
                        value={String(r.days)}
                        onChange={(e) => update(idx, { days: Number(e.target.value.replace(/\D/g, '').slice(0, 4)) || 0 })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        className={inputCls}
                        inputMode="decimal"
                        value={String(r.percent).replace('.', ',')}
                        onChange={(e) => {
                          const v = Number(e.target.value.replace(/[^\d,]/g, '').replace(',', '.'));
                          update(idx, { percent: Number.isFinite(v) ? v : 0 });
                        }}
                      />
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                        onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                        disabled={rows.length <= 1}
                        title="Remover parcela"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={`mt-1 text-xs ${Math.abs(total - 100) > 0.01 ? 'text-red-600' : 'text-slate-500'}`}>
            Soma: {total.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%
          </p>
        </div>

        {example && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
            <p className="font-medium mb-1">
              Exemplo: pedido de {formatCurrencyBRL(1000)} emitido em {formatDateBR(example.orderDate)} com entrega prevista em{' '}
              {formatDateBR(example.deliveryDate)}
            </p>
            <ul className="space-y-0.5">
              {example.installments.map((i, idx) => (
                <li key={idx}>
                  {idx + 1}ª parcela: {formatCurrencyBRL(i.value)} em {formatDateBR(i.due_date)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(error || validation) && <p className="text-sm text-red-600">{error || validation}</p>}
      </div>
    </Dialog>
  );
};

/** Card da aba Pagamento: mostra a condição e abre o editor (providers.write). */
export const ProviderPaymentTermCard: React.FC<{ providerId: number }> = ({ providerId }) => {
  const [term, setTerm] = useState<ProviderPaymentTerm | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await providerService.getPaymentTerm(providerId);
      setTerm(r.term);
      setPaymentMethods(r.paymentMethods);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Erro ao carregar a condição de pagamento');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load]);

  const methodName = term?.payment_method_name
    ?? paymentMethods.find((m) => m.payment_method_id === term?.payment_method_id)?.payment_method_name;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wider">
          <CalendarClock size={14} /> Condição de pagamento
        </p>
        <PermissionGuard permission="providers.write">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} disabled={loading || !!loadError}>
            {term ? 'Editar condição' : 'Cadastrar condição'}
          </Button>
        </PermissionGuard>
      </div>
      {loading ? (
        <p className="text-sm text-slate-400">Carregando…</p>
      ) : loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : term ? (
        <div className="text-sm">
          <p className="font-medium text-slate-900">
            {methodName ?? 'Forma não encontrada'} · {describeTerm(term.installments)}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Usada para gerar as parcelas dos pedidos de compra deste fornecedor
            {term.updated_by ? ` · atualizada por ${term.updated_by}` : ''}
            {term.updated_at ? ` em ${formatDateBR(term.updated_at)}` : ''}.
          </p>
          {term.notes && <p className="text-xs text-slate-600 mt-1">{term.notes}</p>}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Sem condição cadastrada — o pedido de compra usa parcelas mensais.</p>
      )}

      <ProviderPaymentTermModal
        isOpen={editing}
        providerId={providerId}
        initial={term}
        paymentMethods={paymentMethods}
        onClose={() => setEditing(false)}
        onSaved={(t) => {
          setTerm(t);
          setEditing(false);
        }}
      />
    </div>
  );
};
